// src/pages/Cart.tsx

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { DURATION_LABELS, fetchGamesCatalog, type GameInfo } from '../utils/gameCatalog';

const Cart: React.FC = () => {
  const { items, removeFromCart, updateQuantity, clearCart, cartTotal } = useCart();
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState<GameInfo[]>([]);

  useEffect(() => {
    fetchGamesCatalog(token).then(setGames);
  }, [token]);

  const gameNameMap = useMemo(() => new Map(games.map((game) => [game.id, game.name])), [games]);

  const formatGameNames = (gameIds?: string[]) => {
    if (!gameIds || gameIds.length === 0) return null;
    return gameIds.map((id) => gameNameMap.get(id) || id).join(', ');
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/checkout' } } });
      return;
    }
    navigate('/checkout');
  };

  if (items.length === 0) {
    return (
      <div className="section container">
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center', padding: '4rem 0' }}>
          <h1 style={{ marginBottom: '1rem' }}>Shopping Cart</h1>
          <p className="text-secondary" style={{ marginBottom: '2rem' }}>Your cart is empty</p>
          <Link to="/pdf" className="btn btn-primary">
            Browse PDF Resources
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="section container">
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h1 style={{ margin: 0 }}>Shopping Cart</h1>
          <button
            onClick={clearCart}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ef4444',
              backgroundColor: 'white',
              color: '#ef4444',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
            }}
          >
            Clear Cart
          </button>
        </div>

        {/* Cart Items */}
        <div style={{ marginBottom: '2rem' }}>
          {items.map((item) => (
            <div key={item.id} className="card" style={{ padding: '1.5rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <h3 style={{ marginBottom: '0.5rem' }}>{item.title}</h3>
                  {item.category && (
                    <span style={{
                      display: 'inline-block',
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.5rem',
                      backgroundColor: '#e0e7ff',
                      color: '#4f46e5',
                      borderRadius: '4px',
                      fontWeight: '500',
                      marginBottom: '0.5rem'
                    }}>
                      {item.category}
                    </span>
                  )}
                  {item.accessDuration && (
                    <p className="text-secondary" style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                      Access: {DURATION_LABELS[item.accessDuration]}
                      {item.gameIds ? ` • ${formatGameNames(item.gameIds)}` : ''}
                    </p>
                  )}
                  <p className="text-secondary" style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    ${item.price.toFixed(2)} each
                  </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {/* Quantity Controls */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      style={{
                        width: '2rem',
                        height: '2rem',
                        border: '1px solid #d1d5db',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      −
                    </button>
                    <span style={{ minWidth: '2rem', textAlign: 'center', fontWeight: '500' }}>
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      style={{
                        width: '2rem',
                        height: '2rem',
                        border: '1px solid #d1d5db',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      +
                    </button>
                  </div>

                  {/* Item Total */}
                  <div style={{ minWidth: '80px', textAlign: 'right' }}>
                    <p style={{ fontSize: '1.125rem', fontWeight: 'bold', color: 'var(--color-primary)' }}>
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => removeFromCart(item.id)}
                    style={{
                      padding: '0.5rem',
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: '#ef4444',
                      cursor: 'pointer',
                      fontSize: '1.25rem',
                    }}
                    title="Remove from cart"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Cart Summary */}
        <div className="card" style={{ padding: '2rem', backgroundColor: '#f9fafb' }}>
          <div style={{ maxWidth: '400px', marginLeft: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e5e7eb' }}>
              <span className="text-secondary">Subtotal ({items.reduce((sum, item) => sum + item.quantity, 0)} items)</span>
              <span style={{ fontWeight: '500' }}>${cartTotal.toFixed(2)}</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem', fontSize: '1.25rem' }}>
              <span style={{ fontWeight: 'bold' }}>Total</span>
              <span style={{ fontWeight: 'bold', color: 'var(--color-primary)' }}>
                ${cartTotal.toFixed(2)}
              </span>
            </div>

            <button
              className="btn btn-primary"
              style={{ width: '100%', marginBottom: '1rem' }}
              onClick={handleCheckout}
            >
              Proceed to Checkout
            </button>

            <Link
              to="/pdf"
              style={{
                display: 'block',
                textAlign: 'center',
                color: 'var(--color-primary)',
                textDecoration: 'none',
                fontSize: '0.875rem',
              }}
            >
              ← Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
